/**
 * BulkMoveToPipelineModal — explicit "Move to pipeline" bulk action for deals.
 *
 * Unlike BulkChangeStageModal (which keeps deals within one pipeline), this is
 * the deliberate cross-pipeline move: pick a target pipeline, then a stage
 * within it. It submits with allowPipelineChange=true so the server-side guard
 * permits the relocation. Selection may span pipelines — crossing is the intent.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Select } from '@/lib/base/forms/select/select';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import {
  Dialog,
  DialogTrigger,
  Modal,
  ModalOverlay,
} from '@/lib/base/overlays/modal';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import type { CrmObject, FilterGroup } from '@/types/crm';

import { useBulkChangeStageSubmit } from './useBulkChangeStageSubmit';

const DEAL_ENTITY_TYPE = 'ahoy_deal';
const DEAL_STAGE_ASSOCIATION = 'ahoy_deal_to_deal_pipeline_stage';

export interface BulkMoveToPipelineModalProps {
  isOpen: boolean;
  orgId: number;
  selectedObjects: CrmObject[];
  filterGroups?: FilterGroup[];
  excludedEntityIds?: string[];
  search?: string;
  searchProperties?: string[];
  isSelectAllMode: boolean;
  totalCount: number;
  onClose: () => void;
  onAsyncDispatched: (jobId: number, totalCount: number) => void;
  onSyncComplete: () => void;
}

export function BulkMoveToPipelineModal({
  isOpen,
  orgId,
  selectedObjects,
  filterGroups,
  excludedEntityIds,
  search,
  searchProperties,
  isSelectAllMode,
  totalCount,
  onClose,
  onAsyncDispatched,
  onSyncComplete,
}: BulkMoveToPipelineModalProps) {
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(
    null
  );
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);

  // Reset on close so reopening starts fresh.
  useEffect(() => {
    if (!isOpen) {
      setSelectedPipelineId(null);
      setSelectedStageId(null);
    }
  }, [isOpen]);

  const { pipelines, isLoading: isPipelinesLoading } = usePipelines(
    orgId,
    DEAL_ENTITY_TYPE
  );

  const pipelineItems = useMemo(
    () =>
      pipelines
        .filter(p => !!p.entity_id)
        .map(p => ({ id: p.entity_id as string, label: p.label })),
    [pipelines]
  );

  const selectedPipeline = useMemo(
    () => pipelines.find(p => p.entity_id === selectedPipelineId) ?? null,
    [pipelines, selectedPipelineId]
  );

  // Stages of the chosen pipeline, ordered, with seeded entity_ids only.
  const stageItems = useMemo(() => {
    if (!selectedPipeline) {
      return [];
    }
    return [...selectedPipeline.stages]
      .sort((a, b) => (a.properties?.order ?? 0) - (b.properties?.order ?? 0))
      .filter(s => !!s.entity_id)
      .map(s => ({ id: s.entity_id as string, label: s.label }));
  }, [selectedPipeline]);

  const { submit, isSubmitting } = useBulkChangeStageSubmit({
    orgId,
    entityTypeName: DEAL_ENTITY_TYPE,
    associationTypeName: DEAL_STAGE_ASSOCIATION,
    selectedObjects,
    isSelectAllMode,
    filterGroups,
    excludedEntityIds,
    search,
    searchProperties,
    onAsyncDispatched,
    onSyncComplete,
    onClose,
  });

  const displayCount = isSelectAllMode ? totalCount : selectedObjects.length;
  const entityLabel = displayCount === 1 ? 'deal' : 'deals';

  const handlePipelineChange = useCallback(
    (key: unknown) => {
      const pipelineId = typeof key === 'string' ? key : null;
      setSelectedPipelineId(pipelineId);
      // Default the stage to the chosen pipeline's first (ordered) stage so a
      // pipeline pick alone yields a valid, submittable target.
      const pipeline = pipelines.find(p => p.entity_id === pipelineId);
      const firstStage = pipeline
        ? [...pipeline.stages]
            .sort(
              (a, b) => (a.properties?.order ?? 0) - (b.properties?.order ?? 0)
            )
            .find(s => !!s.entity_id)
        : undefined;
      setSelectedStageId(firstStage?.entity_id ?? null);
    },
    [pipelines]
  );

  const handleStageChange = useCallback((key: unknown) => {
    setSelectedStageId(typeof key === 'string' ? key : null);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!selectedStageId || !selectedPipeline) {
      return;
    }
    void submit({
      targetStageId: selectedStageId,
      // This action deliberately crosses pipelines.
      allowPipelineChange: true,
      messages: {
        successTitle: 'Deals moved',
        successDescription: `${displayCount} ${entityLabel} moved to ${selectedPipeline.label}`,
        incomplete: `Move incomplete. Some ${entityLabel} may not have been moved. Try again.`,
      },
    });
  }, [selectedStageId, selectedPipeline, submit, displayCount, entityLabel]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isSubmitting) {
        onClose();
      }
    },
    [isSubmitting, onClose]
  );

  const canConfirm =
    !!selectedPipelineId &&
    !!selectedStageId &&
    !isSubmitting &&
    !isPipelinesLoading;

  const footer = useMemo(
    () => (
      <div className='flex items-center justify-end gap-3'>
        <Button
          color='secondary'
          size='md'
          onClick={onClose}
          isDisabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          color='primary'
          size='md'
          onClick={handleConfirm}
          isDisabled={!canConfirm}
          isLoading={isSubmitting}
        >
          Move
        </Button>
      </div>
    ),
    [onClose, isSubmitting, handleConfirm, canConfirm]
  );

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      <ModalOverlay isDismissable={!isSubmitting}>
        <Modal className='sm:max-w-md'>
          <Dialog
            title='Move to pipeline'
            instance='bulk-move-to-pipeline'
            isCloseDisabled={isSubmitting}
            footer={footer}
          >
            <div className='space-y-3 pb-2'>
              <p className='text-sm text-fg-secondary'>
                Move {displayCount} {entityLabel} to:
              </p>

              {isPipelinesLoading ? (
                <div className='flex items-center gap-2 text-sm text-fg-secondary'>
                  <LoadingIndicator type='line-simple' size='sm' />
                  Loading pipelines...
                </div>
              ) : (
                <>
                  <Select
                    label='Target pipeline'
                    placeholder='Select a pipeline'
                    size='sm'
                    items={pipelineItems}
                    selectedKey={selectedPipelineId}
                    onSelectionChange={handlePipelineChange}
                    isDisabled={isSubmitting}
                    aria-label='Target pipeline'
                  >
                    {item => (
                      <Select.Item id={item.id}>{item.label}</Select.Item>
                    )}
                  </Select>

                  <Select
                    label='Target stage'
                    placeholder='Select a stage'
                    size='sm'
                    items={stageItems}
                    selectedKey={selectedStageId}
                    onSelectionChange={handleStageChange}
                    isDisabled={isSubmitting || !selectedPipelineId}
                    aria-label='Target stage'
                  >
                    {item => (
                      <Select.Item id={item.id}>{item.label}</Select.Item>
                    )}
                  </Select>

                  <p className='text-xs text-fg-tertiary'>
                    Moving deals to a different pipeline changes their pipeline
                    and resets stage-time tracking.
                  </p>
                </>
              )}
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
}
